import Translations from "@translate/utils/member/checkoutConsent";
import { pricingMoney } from "@/src/components/Member/Subscription/onboardingPricingCopy";
import { MemberProduct, PaymentConsent } from "@features/api/memberApi";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 终身周期。类型绑定生成契约 `MemberProduct["pay_cycle"]`，改名会编译失败。
 * 不从 `components/Member/Subscription/constants` 取 `BillingCycle`：那里经 `CurrentPlan/constants` →
 * `useMemberUser` 拉进整条 config / API client 链，且与本文件形成循环依赖（工具层不应依赖组件层）。
 */
const LIFETIME: NonNullable<MemberProduct["pay_cycle"]> = "LIFETIME";

/**
 * 同意文案版本（EDG-552，fe-spec §7 R3）。
 *
 * **改文案必须升版本**；且后端 `consent.valid-versions` 先加入新版本、再发前端，否则下单返回 146205。
 */
export const CONSENT_VERSION = "2026-10-v1";

/** 法务 GAP-8 结论前默认「按钮即同意」；要切勾选框改这里并发版（YAGNI：不接 Remote Config）。 */
export const CONSENT_MODE: "button" | "checkbox" = "button";

type RecurringCycle = "YEAR" | "MONTH" | "WEEK" | "DAY";
type TrialCycle = Exclude<RecurringCycle, "DAY">;

interface SubscriptionCopyVars {
  cta: string;
  price: string;
  locale?: string;
}

interface TrialCopyVars extends SubscriptionCopyVars {
  trialPrice: string;
  trialDays: number;
}

/*
 * 每个周期一整句，而不是 `%{price}/%{period}` 拼接：把单词插进句子在其他语言里语序不通。
 * 字面量必须留在 `I18n.t()` 里，`pnpm run scan` 才收得到。
 */
const SUBSCRIPTION_COPY: Record<
  RecurringCycle,
  (vars: SubscriptionCopyVars) => string
> = {
  YEAR: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you agree to be charged %{price}/year automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
  MONTH: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you agree to be charged %{price}/month automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
  WEEK: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you agree to be charged %{price}/week automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
  DAY: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you agree to be charged %{price}/day automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
};

const PAID_TRIAL_COPY: Record<TrialCycle, (vars: TrialCopyVars) => string> = {
  YEAR: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you agree to pay %{trialPrice} today for a %{trialDays}-day trial. Unless you cancel before it ends, you\'ll be charged %{price}/year automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
  MONTH: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you agree to pay %{trialPrice} today for a %{trialDays}-day trial. Unless you cancel before it ends, you\'ll be charged %{price}/month automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
  WEEK: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you agree to pay %{trialPrice} today for a %{trialDays}-day trial. Unless you cancel before it ends, you\'ll be charged %{price}/week automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
};

/** 0 元试用（渠道 offer 配的免费试用）不能写成「pay $0.00 today」。 */
const FREE_TRIAL_COPY: Record<TrialCycle, (vars: TrialCopyVars) => string> = {
  YEAR: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you start a free %{trialDays}-day trial. Unless you cancel before it ends, you\'ll be charged %{price}/year automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
  MONTH: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you start a free %{trialDays}-day trial. Unless you cancel before it ends, you\'ll be charged %{price}/month automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
  WEEK: (vars) =>
    I18n.t(
      'By clicking "%{cta}", you start a free %{trialDays}-day trial. Unless you cancel before it ends, you\'ll be charged %{price}/week automatically until you cancel. Cancel anytime in Manage Billing.',
      vars,
    ),
};

export interface ConsentCopyInput {
  /** 正价产品：周期与续费金额的来源（试用场景为试用 SKU 的 `renewal_id` 指向的产品） */
  product: MemberProduct;
  /** 试用场景下实际结算的试用 SKU；不传即普通订阅 */
  trialProduct?: MemberProduct | null;
  /**
   * 试用天数。按渠道不同取值来源不同（offer 或商品目录），由调用方用
   * `resolveOnboardingTrialDays(trialProduct, channel)` 按**实际下单渠道**算好传入。
   */
  trialDays?: number;
  /** 按钮文案（已翻译），用于 By clicking "{cta}" */
  ctaLabel: string;
  locale?: string;
}

export interface ConsentCopy {
  /** 正文（已翻译、已插值），不含 Terms / Privacy 链接 */
  text: string;
}

/** 后端价格是字符串（`"299.99"`）；空串 / 非数 / 负数视为缺失，不能当 0 元写进扣款条款。 */
const toAmount = (product?: MemberProduct | null): number | undefined => {
  const raw = product?.price;
  if (raw == null || raw.trim() === "") {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

const isRecurringCycle = (cycle?: string): cycle is RecurringCycle =>
  cycle != null && Object.hasOwn(SUBSCRIPTION_COPY, cycle);

const isTrialCycle = (cycle: RecurringCycle): cycle is TrialCycle =>
  Object.hasOwn(PAID_TRIAL_COPY, cycle);

function buildTrialCopy(
  cycle: RecurringCycle,
  vars: SubscriptionCopyVars,
  input: ConsentCopyInput,
): ConsentCopy | null {
  const trialAmount = toAmount(input.trialProduct);
  const { trialDays } = input;
  if (
    !isTrialCycle(cycle) ||
    trialAmount == null ||
    trialDays == null ||
    !Number.isSafeInteger(trialDays) ||
    trialDays <= 0
  ) {
    return null;
  }
  const trialVars: TrialCopyVars = {
    ...vars,
    trialPrice: pricingMoney(trialAmount, input.locale),
    trialDays,
  };
  const copy = trialAmount === 0 ? FREE_TRIAL_COPY : PAID_TRIAL_COPY;
  return { text: copy[cycle](trialVars) };
}

/**
 * 点击同意文案（fe-spec §4.6 F1-copy）。
 *
 * 返回 null 的情况：终身产品（不展示、不传 consent）；价格 / 周期 / 试用天数任一缺失 ——
 * 宁可不展示也不展示错的扣款条款，调用方此时应禁用下单（试用未就绪与现有 `trialNotReady` 同口径）。
 */
export function buildConsentCopy(input: ConsentCopyInput): ConsentCopy | null {
  const { product, trialProduct, ctaLabel, locale } = input;
  const cycle = product.pay_cycle;
  const amount = toAmount(product);
  if (cycle === LIFETIME || !isRecurringCycle(cycle) || amount == null) {
    return null;
  }
  const vars: SubscriptionCopyVars = {
    cta: ctaLabel,
    price: pricingMoney(amount, locale),
    locale,
  };
  if (trialProduct) {
    return buildTrialCopy(cycle, vars, input);
  }
  return { text: SUBSCRIPTION_COPY[cycle](vars) };
}

/**
 * 不退部分周期费用条款：紧跟扣款条款展示，与之同属 CONSENT_VERSION（沿用原 H5 `SubscribableTerms` 的法务句）。
 * PC / H5（`CheckoutConsent`）与 onboarding 披露共用这一处字面量，保证同一版本展示同一段文本。
 */
export const buildRefundPolicyCopy = (locale?: string): string =>
  I18n.t(
    "No refunds for partial billing periods, except as required by applicable law.",
    { locale },
  );

/** `/payment/check` 的 consent：终身返回 undefined；订阅与试用 SKU 返回当前版本。 */
export function buildConsentPayload(
  product: MemberProduct,
): PaymentConsent | undefined {
  if (product.pay_cycle === LIFETIME) {
    return undefined;
  }
  return { version: CONSENT_VERSION, accepted: true };
}
